import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useOrpcTanstackQueryUtils } from '../orpc'

export function useTripGetAll(filters?: {
  aircraftId?: string
  searchQuery?: string
  status?: string
  startDate?: string
  endDate?: string
}) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(orpc.trip.getAll.queryOptions({ input: { ...filters } }))
}

export function useTripGetById(id: string) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(orpc.trip.getById.queryOptions({ input: { id }, enabled: !!id }))
}

export function useTripGetMyNextFlight(crewId: string | undefined) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery({
    ...orpc.trip.getMyNextFlight.queryOptions({ input: { crewId: crewId! } }),
    enabled: !!crewId,
  })
}

export function useTripGetMyTripCount(crewId: string | undefined) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery({
    ...orpc.trip.getMyTripCount.queryOptions({ input: { crewId: crewId! } }),
    enabled: !!crewId,
  })
}

export function useTripGetMyTrips(
  crewId: string | undefined,
  filters?: {
    searchQuery?: string
  }
) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery({
    ...orpc.trip.getMyTrips.queryOptions({
      input: { crewId: crewId!, ...filters },
    }),
    enabled: !!crewId,
  })
}

export function useTripCreate() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.trip.create.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
        queryClient.invalidateQueries({ queryKey: orpc.roster.key() })
      },
    })
  )
}

export function useTripUpdate(tripId: string) {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.trip.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.trip.getById.key({ input: { id: tripId } }),
        })
      },
    })
  )
}

export function useTripDelete() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.trip.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
        queryClient.invalidateQueries({ queryKey: orpc.roster.key() })
      },
    })
  )
}

// Every trip the current crew member still has to fly, each with their own legs
// and an all-or-nothing `accepted` flag. Drives the "Upcoming Trips" accordion.
export function useTripGetMyUpcomingTrips(enabled = true) {
  const orpc = useOrpcTanstackQueryUtils()

  return useQuery({
    ...orpc.trip.getMyUpcomingTrips.queryOptions(),
    enabled,
  })
}

// Acknowledge the current crew member's outstanding assignments on one trip
export function useTripAcknowledgeMyTrip() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.trip.acknowledgeMyTrip.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
      },
      onError: (error) => {
        console.error('Error acknowledging trip:', error)
      },
    })
  )
}

export function useTripLegGetById(tripLegId?: string) {
  const orpc = useOrpcTanstackQueryUtils()

  return useQuery({
    ...orpc.trip.getByIdTripLeg.queryOptions({ input: { id: tripLegId! } }),
    enabled: !!tripLegId,
  })
}

// Add Trip Leg to a Trip
export function useTripLegAdd() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.trip.addLeg.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
        // Flight log prefill (route/crew/PAX) is derived from the leg
        queryClient.invalidateQueries({ queryKey: orpc.flightLog.key() })
      },
      onError: (error) => {
        console.error('Error adding leg:', error)
      },
    })
  )
}

export function useTripLegUpdate() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.trip.updateLeg.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
        // Flight log prefill (route/crew/PAX) is derived from the leg
        queryClient.invalidateQueries({ queryKey: orpc.flightLog.key() })
      },
    })
  )
}

export function useTripLegDelete() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.trip.deleteLeg.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
        queryClient.invalidateQueries({ queryKey: orpc.flightLog.key() })
      },
    })
  )
}

// Get a single note by ID
export function useNoteGetById(noteId?: string) {
  const orpc = useOrpcTanstackQueryUtils()

  return useQuery({
    ...orpc.trip.getNoteById.queryOptions({ input: { id: noteId! } }),
    enabled: !!noteId,
  })
}

// Add a note to a trip
export function useNoteAdd(tripId: string) {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.trip.addNote.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.trip.getById.key({ input: { id: tripId } }),
        })
      },
    })
  )
}

// Update a note
export function useNoteUpdate(tripId: string) {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.trip.updateNote.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.trip.getById.key({ input: { id: tripId } }),
        })
      },
    })
  )
}

// Delete a note
export function useNoteDelete(tripId: string) {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.trip.deleteNote.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.trip.getById.key({ input: { id: tripId } }),
        })
      },
    })
  )
}

// ============ CREW PERSONAL NOTES ============

export function useCrewNotesList() {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(orpc.trip.listCrewNotes.queryOptions({ input: {} }))
}

export function useCrewNoteAdd() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.trip.addCrewNote.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.trip.listCrewNotes.key() })
      },
    })
  )
}

export function useCrewNoteUpdate() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.trip.updateCrewNote.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.trip.listCrewNotes.key() })
      },
    })
  )
}

export function useCrewNoteDelete() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.trip.deleteCrewNote.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.trip.listCrewNotes.key() })
      },
    })
  )
}

export function useTripUpdateComplianceDocuments(tripId: string) {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.trip.updateComplianceDocuments.mutationOptions({
      onSuccess: () => {
        // Invalidate trip query to refetch with new documents
        queryClient.invalidateQueries({
          queryKey: orpc.trip.getById.key({ input: { id: tripId } }),
        })
      },
    })
  )
}

export function useTripLegUpdateComplianceDocuments() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.trip.updateLegComplianceDocuments.mutationOptions({
      onSuccess: (_data, variables) => {
        // Invalidate trip leg query to refetch with new documents
        queryClient.invalidateQueries({
          queryKey: orpc.trip.getByIdTripLeg.key({ input: { id: variables.tripLegId } }),
        })
      },
    })
  )
}

// Set trip state with validated transitions
export function useTripSetState() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.trip.setTripState.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
      },
    })
  )
}

// Toggle a flight's PAX onboarding status (Pending / Boarded)
export function useTripLegSetPaxBoarded() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.trip.setLegPaxBoarded.mutationOptions({
      onMutate: async ({ tripLegId, boarded }) => {
        const { queryKey } = orpc.trip.getByIdTripLeg.queryOptions({ input: { id: tripLegId } })
        await queryClient.cancelQueries({ queryKey })

        const previousLeg = queryClient.getQueryData(queryKey)
        queryClient.setQueryData(queryKey, (old: any) =>
          old
            ? {
                ...old,
                paxBoarded: boarded,
                paxBoardedAt: boarded ? new Date().toISOString() : null,
              }
            : old
        )

        return { previousLeg, queryKey }
      },
      onError: (_err, _input, context) => {
        if (context?.previousLeg !== undefined) {
          queryClient.setQueryData(context.queryKey, context.previousLeg)
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: orpc.trip.key() })
      },
    })
  )
}

// Check if a leg overlaps with existing legs in a trip
export function useTripCheckLegOverlap(params: {
  tripId?: string
  departureDateTime?: Date
  arrivalDateTime?: Date
  excludeLegId?: string
}) {
  const { tripId, departureDateTime, arrivalDateTime, excludeLegId } = params
  const orpc = useOrpcTanstackQueryUtils()

  return useQuery({
    ...orpc.trip.checkLegOverlap.queryOptions({
      input: {
        tripId: tripId!,
        departureDateTime: departureDateTime!,
        arrivalDateTime: arrivalDateTime!,
        ...(excludeLegId && { excludeLegId }),
      },
    }),
    enabled: !!tripId && !!departureDateTime && !!arrivalDateTime,
  })
}

// Validate crew availability for a trip leg (roster status + flight conflicts)
export function useTripValidateCrewAvailability(params: {
  crewIds: string[]
  departureDate?: Date
  excludeLegId?: string
  aircraftId?: string
}) {
  const { crewIds, departureDate, excludeLegId, aircraftId } = params
  const orpc = useOrpcTanstackQueryUtils()
  const departureDateStr = departureDate ? dayjs(departureDate).format('YYYY-MM-DD') : undefined

  return useQuery({
    ...orpc.trip.validateCrewAvailability.queryOptions({
      input: {
        crewIds,
        departureDate: departureDateStr!,
        ...(excludeLegId && { excludeLegId }),
        ...(aircraftId && { aircraftId }),
      },
    }),
    enabled: crewIds.length > 0 && !!departureDateStr,
  })
}

// Get available crew for a trip leg (those with valid roster entries)
// Can be called with tripLegId (edit mode) or with tripId + dates (create mode)
// When both leg datetimes are provided, flight conflicts are checked by actual
// time-window overlap instead of the whole departure day.
export function useTripLegGetAvailableCrew(params: {
  startDate?: Date
  departureDateTime?: Date
  arrivalDateTime?: Date
  excludeLegId?: string
  aircraftId?: string
}) {
  const { startDate, departureDateTime, arrivalDateTime, excludeLegId, aircraftId } = params
  const orpc = useOrpcTanstackQueryUtils()
  const startDateStr = startDate ? dayjs(startDate).format('YYYY-MM-DD') : undefined

  return useQuery({
    ...orpc.trip.getAvailableCrew.queryOptions({
      input: {
        startDate: startDateStr,
        ...(departureDateTime && arrivalDateTime && { departureDateTime, arrivalDateTime }),
        ...(excludeLegId && { excludeLegId }),
        ...(aircraftId && { aircraftId }),
      },
    }),
    enabled: !!startDateStr,
    staleTime: 0,
    // The key embeds the leg datetimes, so every schedule edit is a "new" query.
    // Without previous-data retention, isPending flips true on each edit and
    // FlightDrawer's edit mode swaps the form for skeletons — unmounting the
    // very input being typed into (focus + draft lost mid-keystroke).
    placeholderData: keepPreviousData,
  })
}

// Get trip legs by crew IDs and date range (for roster gantt view)
export function useTripLegsByCrewAndDateRange(params: {
  crewIds: string[]
  startDate?: Date
  endDate?: Date
  keepPreviousData?: boolean
}) {
  const { crewIds, startDate, endDate, keepPreviousData: keepPrev } = params
  const orpc = useOrpcTanstackQueryUtils()

  return useQuery({
    ...orpc.trip.getTripLegsByCrewAndDateRange.queryOptions({
      input: {
        crewIds,
        startDate: startDate!,
        endDate: endDate!,
      },
    }),
    enabled: crewIds.length > 0 && !!startDate && !!endDate,
    // Opt-in: retain prior range's legs while a wider range loads (Gantt scroll).
    ...(keepPrev ? { placeholderData: keepPreviousData } : {}),
  })
}
